import { useIsMobile } from "../../hooks/useMobile";
import { ReactElement, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "../../components/ui/sidebar";
import { useSetPageTitle } from "../../hooks/useSetPageTitle";
import { useViewSurveyData } from "../view-survey/useViewSurveyData";
import { SurveyResultsSidebar } from "./survey-results-sidebar";
import ResultsQuestionList from "../view-survey/components/results-question-list.component";
import { combineQuestionStats } from "../view-survey/view-survey.helpers";
import { SurveyCompleteDialog, useSurveyCompleteDialogSetup } from "../view-survey/components/survey-complete-dialog.component";
import { useSurveySummary } from "../view-survey/survey-action-plan/useSurveySummary";
import { useSurveyMediaPlan } from "../view-survey/survey-action-plan/useSurveyMediaPlan";
import TrendsTab from "../results/tabs/trends.tab";

const SHARE_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'trends', label: 'Trends' },
] as const;

const SurveyResultsPage = (): ReactElement => {
  useSetPageTitle('Survey results');
  const navigate = useNavigate();
  const { tab: shareTab } = useParams<{ shareId?: string; tab?: string }>();
  const {
    isShare,
    surveyId,
    stats,
    survey,
    summary,
    showSummaries,
  } = useViewSurveyData();
  const { isMobile } = useIsMobile();
  const { showDialog, setShowDialog } = useSurveyCompleteDialogSetup({
    mode: isShare ? 'share' : 'view',
    surveyId: survey?.extSurveyId,
    status: survey?.surveyStatus,
  });

  const showTrendsTab = survey?.hasTrends ?? false;

  const questions = useMemo(() => {
    return combineQuestionStats(stats?.questions, survey?.questions, showSummaries ? summary?.questions : undefined);
  }, [stats, survey, summary, showSummaries]);

  const { isLoadingSummary, isGeneratingSummary, createSummary } = useSurveySummary(surveyId, survey?.surveyStatus, isShare);
  const { isLoadingPlan, isGeneratingMediaPlan, createMediaPlan } = useSurveyMediaPlan(surveyId, survey?.surveyStatus, isShare);

  const activeShareTab = shareTab === 'trends' && showTrendsTab ? 'trends' : 'overview';
  const onShareTabChange = (tabId: string) => {
    if (tabId === 'trends' && !showTrendsTab) return;
    if (tabId === 'trends') {
      navigate(`/survey/share/${surveyId}/trends`);
    } else {
      navigate(`/survey/share/${surveyId}`);
    }
  };

  return (
    <SidebarProvider>
      <SurveyCompleteDialog
        surveyId={surveyId}
        showDialog={showDialog}
        setShowDialog={setShowDialog}
      />
      <div className={`w-full p-4 ${isShare ? 'p-8' : 'md:p-1'} ${showDialog ? 'max-h-screen overflow-y-hidden' : ''}`}>
        {isMobile && (
          <div className="flex size-12 items-center justify-center">
            <SidebarTrigger />
          </div>
        )}
        {isShare && (
          <div className="flex gap-1 mb-4 p-1 bg-gray-200 rounded-lg w-fit">
            {SHARE_TABS.filter((t) => (t.id === 'trends' ? showTrendsTab : true)).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => onShareTabChange(tab.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeShareTab === tab.id ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
        {(!isShare || activeShareTab === 'overview') && (
          <ResultsQuestionList
            questions={questions}
            aiMaxCompletes={survey?.maxAICompletes ?? undefined}
            humanMaxCompletes={survey?.maxCompletes ?? undefined}
            surveyId={surveyId}
            audienceId={survey?.audience ?? ''}
            isShare={isShare}
          />
        )}
        {isShare && activeShareTab === 'trends' && <TrendsTab />}
      </div>
      <SurveyResultsSidebar
        surveyId={surveyId}
        isShare={isShare}
        createSummary={createSummary}
        createMediaPlan={createMediaPlan}
      />
    </SidebarProvider>
  );
};

export default SurveyResultsPage;
